import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { SessionAuth } from 'supertokens-auth-react/recipe/session';
import MockupDisplayCard from './MockupDisplayCard';
import { api } from '../apiClient';

const MockupsPage = () => {
  const [mockups, setMockups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMockupIds, setSelectedMockupIds] = useState(new Set()); // Use a Set for efficient lookups
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMockups = async () => {
      try {
        const response = await api.get(`/mockups`);
        setMockups(response.data);
      } catch (err) {
        setError('Failed to fetch mockups.');
        console.error('Error fetching mockups:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMockups();
  }, []);

  const handleSelectMockup = (mockupId) => {
    setSelectedMockupIds((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(mockupId)) {
        newSelected.delete(mockupId);
      } else {
        newSelected.add(mockupId);
      }
      return newSelected;
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedMockupIds.size === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedMockupIds.size} selected mockup(s)?`)) {
      return;
    }

    setLoading(true);
    try {
      // Convert Set to Array for Promise.all
      const deletePromises = Array.from(selectedMockupIds).map((id) =>
        api.delete(`/mockups/${id}`)
      );
      await Promise.all(deletePromises);

      // Filter out deleted mockups from the state
      setMockups((prevMockups) =>
        prevMockups.filter((mockup) => !selectedMockupIds.has(mockup.id))
      );
      setSelectedMockupIds(new Set()); // Clear selection
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Error deleting selected mockups:', err);
      setError('Failed to delete one or more mockups.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SessionAuth>
      <div className="container mx-auto p-4 bg-background rounded-lg shadow-lg tour-mockups">
        <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">Your Mockups</h2>

        <div className="flex justify-end mb-4">
          {/* Start: Delete Button Block */}
          {selectedMockupIds.size > 0 ? (
            <button
              onClick={handleDeleteSelected}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Delete Selected ({selectedMockupIds.size})
            </button>
          ) : (
            <button
              disabled
              className="bg-gray-400 text-white font-bold py-2 px-4 rounded-md cursor-not-allowed"
            >
              Delete Selected
            </button>
          )}
          {/* End: Delete Button Block */}
        </div>

        {loading ? (
          <div className="text-center mt-8">Loading mockups...</div>
        ) : error ? (
          <div className="text-center mt-8 text-red-500">Error: {error}</div>
        ) : mockups.length === 0 ? (
          <p className="text-center text-gray-600">No mockups generated yet. Go to the <Link to="/design" className="text-primary hover:underline">design page</Link> to create one!</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {mockups.map((mockup) => (
              <MockupDisplayCard
                key={mockup.id}
                mockup={mockup}
                isSelected={selectedMockupIds.has(mockup.id)}
                onSelect={handleSelectMockup}
              />
            ))}
          </div>
        )}
      </div>
    </SessionAuth>
  );
};

export default MockupsPage;